/**
 * Telling "you may not look" apart from "there is nothing here" — the shared
 * predicate for every CRM admin surface.
 *
 * Generalised from G18's `isForbiddenDeliveryError`, which said in its own
 * docblock that it "must not grow into" a general CRM-role hook and that
 * anything wider was its own piece of work. This IS that work
 * (STATUS-admin-gate-reconciliation.md, decision 2), so the G18 original is
 * gone and its three call sites moved here: exactly one implementation.
 *
 * ── WHY THE DISTINCTION MATTERS ────────────────────────────────────────────
 *
 * Several CRM surfaces gate client-side on the platform/tenant/instance notion
 * of admin while the server gates on the CRM ROLE (`requireCrmRole` → 403
 * `INSUFFICIENT_CRM_ROLE`). A platform admin who is a CRM counselor therefore
 * passes the client guard and is refused by every request. Rendered naively,
 * that refusal becomes an empty list ("No delivery destinations yet") beside a
 * button whose only possible outcome is another 403 — a lie about the DATA that
 * is really a statement about the CALLER, plus a dead end.
 *
 * A surface takes the server's answer as the truth: an empty grid may only ever
 * mean "there are none", and a 403 gets the permission state AND retires the
 * actions it would otherwise offer.
 */

/**
 * Did this RTK Query error come back 403?
 *
 * NARROW ON PURPOSE. A `FETCH_ERROR` (the server is unreachable), a `TIMEOUT_ERROR`
 * or a 500 is NOT a permission answer — the caller may well be entitled to the
 * data and simply could not get it — so those must keep surfacing as errors.
 * Conflating them would tell an admin they lack permission every time the
 * backend hiccups, and send them to the wrong person to fix it.
 *
 * 401 is excluded too, and that is not an oversight: an expired session is an
 * AUTHENTICATION problem with a different remedy (sign in again), and the app's
 * auth layer already owns it.
 *
 * `originalStatus` is covered because fetchBaseQuery reports a non-JSON body as
 * `{ status: "PARSING_ERROR", originalStatus: 403 }`, which an HTML error page
 * from a proxy in front of the API produces.
 */
export function isCrmForbiddenError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const e = error as { status?: unknown; originalStatus?: unknown };
  return e.status === 403 || e.originalStatus === 403;
}

/**
 * ⚠️ THERE WAS AN `anyCrmForbidden(...errors)` HERE. It was written for the
 * "several queries behind one gate" case and then never called: DataExportPage,
 * the only page with that shape, does not have several ERRORS to fold — it has
 * one query error plus a boolean reported UP from DeliveryLogPanel, which a
 * variadic error-folder cannot take. So it was an exported helper with a test
 * and no caller, which reads to the next person as a sanctioned pattern and
 * invites a second way of doing this. Deleted rather than retro-fitted.
 *
 * The principle it encoded still holds and is enforced at the call site: a
 * surface that is half "here is your data" and half "you may not look" is worse
 * than either answer alone, so a page behind ONE server gate gives ONE answer.
 * DataExportPage does that by ORing the delivery-log refusal into its own
 * `forbidden` before the whole-surface check — see the comment there.
 */
